import React from 'react'
import { Redirect } from 'react-router-dom'
import { useMyPermissions, Action } from '../hooks/useMyPermissions'

interface Props {
  module?: string
  action?: Action
  projectId?: string
  // Some routes are gated by a backend policy that can't be expressed as a
  // flat module/action pair (e.g. Query.submittal's named-reviewer exception —
  // a reviewer named on the record may open it with zero module rights).
  // 'server' skips the client-side module check entirely and trusts the
  // route's own component to render its existing error state on denial.
  guardPolicy?: 'client' | 'server'
  children: React.ReactNode
}

/**
 * Blocks direct-URL access to a route whose module the current user cannot
 * access (VIEW by default; routes may require a stronger action via
 * `moduleAction`, e.g. project settings require PROJECT.EDIT).
 * Fails open while permissions are still loading — the backend remains the
 * source of truth and will reject any unauthorized operation regardless.
 */
const ModuleGuard: React.FC<Props> = ({ module, action = 'VIEW', projectId, guardPolicy, children }) => {
  const { perms, loading, can, recordScoped } = useMyPermissions(projectId)

  if (guardPolicy === 'server') return <>{children}</>
  if (!module) return <>{children}</>
  if (loading || !perms) return <>{children}</>
  if (can(action, module)) return <>{children}</>
  // 1b-B spec §8: a contractor's representative enters the module; the server shows him his records only.
  if (action === 'VIEW' && recordScoped(module)) return <>{children}</>
  return <Redirect to="/dashboard" />
}

export default ModuleGuard
